import React from 'react';
import { Calibration } from '../views/calibration';
import { UserData } from '../views/user-data';
import { GlobalLogic } from '../../shared/logic/global-logic';
import { Intro } from '../views/intro';
import { VidebisTest } from '../../shared/util/types';
import { GammaCorrection } from '../views/gamma-correction';
import { GammaCorrectionLogic } from '../../eye-test/logic/gamma-correction-logic';
import MediaQuery from 'react-responsive';
import { getQuery } from '../../shared/util/functions';
import '../styles/screen-config.scss';
import { postTelemetry } from '../../result/util/axios';
import { DistanceFromScreen } from '../views/distance-from-screen';
import { getConfigTelemetryData } from '../../result/util/telemetry';
import { IGeneralTelemetryData } from '../../result/util/typings';

interface Props {
	history: any;
	location: any;
}

interface State {
	step: number;
	scaleFactor: number;
	color: string;
	userHeight: number | null;
	userYearOfBirth: number | null;
	disableLast: boolean;
}

export class ScreenConfigController extends React.PureComponent<Props, State> {

	private _globalLogic: GlobalLogic = GlobalLogic.getInstance();
	private _gammaCorrectionLogic: GammaCorrectionLogic = GammaCorrectionLogic.getInstance();

	state: State = {
		step: 1,
		scaleFactor: this._globalLogic.scaleFactor,
		color: this._gammaCorrectionLogic.getCurrentColor(),
		userHeight: this._globalLogic.height,
		userYearOfBirth: this._globalLogic.yearOfBirth,
		disableLast: true
	};

	componentDidMount(): void {
		let newStep: number = +this.props.location.search.split('=')[1];
		if (isNaN(newStep)) {
			newStep = 1;
		}
		this.changeStep(newStep);
	}

	componentDidUpdate(prevProps: Readonly<Props>): void {
		if (prevProps.location.search !== this.props.location.search) {
			const qStr: string = this.props.location.search;
			const qObj = getQuery(qStr);
			if (qObj && qObj.step) {
				this.changeStep(+qObj.step);
			}
		}
	}

	changeStep = (step: number): void => {
		if (this.state.step !== step) {
			this.setState({ step });
		}
	};

	goToNext = (): void => {
		postTelemetry('config', this.getTelemetryData());
		this.props.history.push('/setup?step=' + this.getNextStep());
	};

	goBack = (): void => {
		this.props.history.push('/setup?step=' + this.getPreviousStep());
	};

	getNextStep = (): number => {
		return this.state.step + 1;
	};

	getPreviousStep = (): number => {
		return this.state.step - 1;
	};

	getTelemetryData = (): IGeneralTelemetryData => {
		const data = getConfigTelemetryData();
		let _data;
		switch (this.state.step) {
			case 2:
				console.log('GOT DATA', data);
				const { scaleFactor } = data;
				_data = { scaleFactor };
				break;
			case 3:
				const { gammaFactor, calibrationColor } = data;
				_data = { gammaFactor, calibrationColor };
				break;
			case 4:
				const { height, yearOfBirth } = data;
				_data = { height, yearOfBirth };
				break;
			default:
				_data = 'done';
		}
		return { step: this.state.step, data: _data };
	}

	handleScaleChange = (decrease: boolean = false): void => {
		if (
			(!decrease && this.state.scaleFactor >= 1.76) ||
			(decrease && this.state.scaleFactor <= 0.5)
		) {
			return;
		}
		const newScale = this.state.scaleFactor + (decrease ? -0.02 : 0.02);
		this.setState({ scaleFactor: newScale });
		this._globalLogic.scaleFactor = newScale;
	};

	handleColorChange = (makeDarker: boolean = false): void => {
		this._gammaCorrectionLogic.changeColor(makeDarker);
		this.setState({ color: this._gammaCorrectionLogic.getCurrentColor() });
	};

	handleUserHeightChange = (value: number): void => {
		this._globalLogic.distance = value;
		this.setState({ userHeight: value });
	};

	handleUserYearOfBirthChange = (value: number): void => {
		this._globalLogic.yearOfBirth = value;
		this.setState({ userYearOfBirth: value });
	};

	handleDisableContinue = (value: boolean): void => {
		this.setState({ disableLast: value });
	};

	startTest = (test: VidebisTest): void => {
		this.props.history.push('/test-explanation?test=' + test);
	};

	startAllTests = (): void => {
		postTelemetry('config', this.getTelemetryData());
		this.props.history.push('/test-explanation?test=eye-test&all-tests=true');
	};

	getView = (): any => {
		switch (this.state.step) {
			case 1:
				return <Intro onPress={this.goToNext} />;
			case 2:
				return <Calibration
					scaleFactor={this.state.scaleFactor}
					onScaleChange={this.handleScaleChange}
					onPress={this.goToNext}
					onBack={this.goBack}
				/>;
			case 3:
				return <GammaCorrection
					onPress={this.goToNext}
					onBack={this.goBack}
					color={this.state.color}
					gammaFactor={this._gammaCorrectionLogic.getGammaCorrection()}
					onColorChange={this.handleColorChange}
				/>;
			case 4:
				return <UserData
					onPress={this.goToNext}
					onBack={this.goBack}
					height={this.state.userHeight || undefined}
					onHeightChange={this.handleUserHeightChange}
					yearOfBirth={this.state.userYearOfBirth || undefined}
					onYearOfBirthChange={this.handleUserYearOfBirthChange}
					onInvalidData={this.handleDisableContinue}
				/>;
			case 5:
				return <DistanceFromScreen
					onPress={this.startAllTests}
					onBack={this.goBack}
				/>;
			// case 6:
			// 	return <TestPicker
			// 		onBack={this.goBack}
			// 		onShouldStartVisusTest={() => this.startTest('eye-test')}
			// 		onShouldStartContrastTest={() => this.startTest('contrast-test')}
			// 		onShouldStartMichelsonTest={() => this.startTest('michelson-test')}
			// 		onShouldStartTextTest={() => this.startTest('text-test')}
			// 		onShouldStartAllTests={this.startAllTests}
			// 	/>;
			default:
				this.props.history.push('/setup?step=1');
		}
	};

	getMain = (inner: JSX.Element): JSX.Element => {
		return (
			<>
				<MediaQuery maxWidth={800}>
					<main className={'screen-config--container'}>
						{inner}
					</main>
				</MediaQuery>
				<MediaQuery minWidth={801}>
					<main className={'screen-config--container'}>
						{inner}
					</main>
				</MediaQuery>
			</>
		);
	};

	render(): any {
		return (
			<main className={'screen-config--container'}>
				<div className={'screen-config__content'}>
					{this.getView()}
				</div>
			</main>
		);
	}

}

